import {
  UpdateEventListener,
  FireableUpdateEventListener
} from 'utils/UpdateEventListener';
import { AdvertiserManager, DefaultAdvertiserManager } from 'core/advertiser/AdvertiserManager';
import { ADVERTISER_COLUMNS, advertiserListColumnSettings } from './advertiserListColumnSettings';
import _ from 'lodash';
import { AdvertisersWithPagination } from 'ws/AdvertiserWebService';
import { Pagination } from 'core/pagination/Pagination';
import { SortableList, AbstractSortableList } from 'containers/Common/AbstractSortableList';
import { PartnershipMode } from 'core/agency/Agency';

export interface AdvertiserListModel extends SortableList {
  readonly agencyId?: number;
  readonly isSysAdmin: boolean;
  readonly selectedPartnershipModeIds: number[];
  readonly state: AdvertiserListState;
  readonly event: UpdateEventListener<AdvertiserListModel>;
  readonly columnSettings: Array<any>;
  readonly searchString: string;
  init (page?: number): Promise<boolean>;
  handleOnSearch (searchString: string);
  reload (): Promise<boolean>;
  sortAdvertisers (field, direction): void;
  onUnmount (handler: number): void;
}

export type AdvertiserListProps = {
  readonly model: AdvertiserListModel;
};

export type AdvertiserListState = {
  readonly loading: boolean;
  readonly viewModelData: Array<any>;
  readonly pagination?: Pagination;
};

export class DefaultAdvertiserListModel extends AbstractSortableList implements AdvertiserListModel {
  event: FireableUpdateEventListener<AdvertiserListModel>;
  loading: boolean;
  advertiserManager: AdvertiserManager;
  advertiserWithPagination?: AdvertisersWithPagination;
  columnSettings: Array<any>;
  viewModelData: Array<any>;
  agencyId?: number;
  readonly isSysAdmin: boolean;
  searchString: string;
  debouncedInit: any;
  pagination?: Pagination;

  constructor (
    agencyId: number | undefined,
    isSysAdmin: boolean,
    private getPartnershipModeId: () => PartnershipMode | undefined = () => undefined,
    advertiserManager: AdvertiserManager = new DefaultAdvertiserManager()
  ) {
    super(ADVERTISER_COLUMNS.NAMEID, 'desc');
    this.event = new FireableUpdateEventListener<AdvertiserListModel>();
    this.loading = true;
    this.agencyId = agencyId;
    this.isSysAdmin = isSysAdmin;
    this.advertiserManager = advertiserManager;
    this.columnSettings = advertiserListColumnSettings(isSysAdmin);
    this.searchString = '';
    this.debouncedInit = _.debounce(this.init.bind(this), 1000);
    this.viewModelData = [];
  }

  get state (): AdvertiserListState {
    return {
      loading: this.loading,
      viewModelData: this.viewModelData,
      pagination: this.pagination
    };
  }

  async init (page: number | undefined = 1): Promise<boolean> {
    this.updateState(true);
    try {
      let sort = this.sortField;
      const pageable = { page, sizePerPage: 10, sort, direction: this.sortOrder };
      this.advertiserWithPagination = await this.advertiserManager.getAdvertisers(
        this.agencyId,
        pageable,
        this.searchString,
        this.selectedPartnershipModeIds
      );
      this.pagination = this.advertiserWithPagination.pagination;
      this.updateViewModelData();
      return true;
    } catch (e) {
      this.updateState(false);
      return false;
    }
  }

  // 權限守在讀取點，日後新增設定篩選值的路徑不必再各自補守門。
  get selectedPartnershipModeIds (): number[] {
    const partnershipModeId = this.isSysAdmin ? this.getPartnershipModeId() : undefined;
    return partnershipModeId === undefined ? [] : [partnershipModeId];
  }

  sortAdvertisers = (field, direction) => {
    const currentSortSetting = this.sortDescriptor[0];
    if (field === currentSortSetting.dataField && direction === currentSortSetting.order) {
      return;
    }
    this.handleSort(field, direction);
    this.init();
  }

  handleOnSearch = (searchString: string) => {
    this.searchString = searchString;
    if (this.searchString === '') {
      this.reload();
    } else {
      this.debouncedInit();
    }
  }

  reload = async (): Promise<boolean> => {
    // 搜尋是 1 秒 debounce。不取消的話這裡的立即請求會與稍後觸發的那次疊在一起，
    // 而 init 沒有請求序號，晚回來的那個會覆蓋先回來的。
    this.debouncedInit && this.debouncedInit.cancel();
    return this.init();
  }

  updateViewModelData () {
    if (!this.advertiserWithPagination) {
      return {};
    }
    this.viewModelData = this.advertiserWithPagination.advertisers.map(advertiser => {
      return {
        name: advertiser.advertiserName,
        [ADVERTISER_COLUMNS.NAMEID]: advertiser.id,
        [ADVERTISER_COLUMNS.STATE]: advertiser.state,
        [ADVERTISER_COLUMNS.PARTNERSHIP_MODE]: advertiser.partnershipModeId
      };
    });
    this.updateState();
  }

  onUnmount (handler: number): void {
    this.event.remove(handler);
  }

  updateState (loading: boolean = false) {
    this.loading = loading;
    this.event.fireEvent(this);
  }
}
